import { Suspense } from 'react';

import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import {
	createFileRoute,
	Link,
	Outlet,
	useChildMatches,
} from '@tanstack/react-router';

import {
	Box,
	Container,
	LinearProgress,
	Paper,
	Tab,
	Tabs,
} from '@mui/material';

import {
	riskQueries,
	useMutateRiskClose,
	useMutateRiskReopen,
} from '@repo/api/risk/risks';
import { SingleRisk } from '@repo/api/risk/risks.types';
import { ReportStatusBody } from '@repo/api/shared/report.types';
import { useUser } from '@repo/auth';
import { ReasonDialogProvider } from '@repo/features/reasonDialog';

import { RiskHeader } from './_risk/-components/RiskHeader';
import { Route as ActivityRoute } from './_risk/activity';
import { Route as CalculationsRoute } from './_risk/calculations';
import { Route as DataRoute } from './_risk/data';
import { Route as DocumentsRoute } from './_risk/documents';
import { Route as DetailsRoute } from './_risk/index';
import { Route as ReportRoute } from './_risk/report';

function needsRefetch(risk: SingleRisk) {
	return risk.riskDocuments.some((d) => d.documentStatus === 'Processing');
}

function needReportRefetch(risk: SingleRisk, reportStatus: ReportStatusBody) {
	return (
		reportStatus.status === 'NoReport' &&
		risk.riskDocuments.length > 0 &&
		risk.riskDocuments.every((d) => d.documentStatus === 'Completed')
	);
}

export const Route = createFileRoute('/_risks/risk/$riskId/_risk')({
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(riskQueries.id(params.riskId));
	},
	component: function RiskPage() {
		const params = Route.useParams();

		const { permissions } = useUser();

		const [{ data: risk }, { data: reasons }] = useSuspenseQueries({
			queries: [
				riskQueries.id(params.riskId, {
					refetchInterval: (risk) => (needsRefetch(risk) ? 5000 : false),
				}),
				riskQueries.riskReasons(),
			],
		});

		const { data: reportStatus } = useQuery(
			riskQueries.reportStatus(
				params.riskId,
				permissions.includes('risk.report'),
				{
					refetchInterval: (reportStatus) =>
						needReportRefetch(risk, reportStatus) ? 5000 : false,
				},
			),
		);

		const hasReport = reportStatus && reportStatus.status !== 'NoReport';

		const matchedId = useChildMatches({
			select: (match) => match[0]?.routeId,
		});

		const { mutateAsync: close, isPending: isClosing } = useMutateRiskClose(
			params.riskId,
		);
		const { mutateAsync: reopen, isPending: isReopening } = useMutateRiskReopen(
			params.riskId,
		);

		return (
			<ReasonDialogProvider
				type="Risk"
				stage={risk.stage}
				reasons={reasons}
				close={async (...params) => void (await close(...params))}
				reopen={async (...params) => void (await reopen(...params))}
				isProcessing={isClosing || isReopening}
			>
				<Container
					maxWidth="lg"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						flexGrow: 1,
						'& .MuiButton-root': {
							whiteSpace: 'nowrap',
						},
						mt: 2,
					}}
				>
					<RiskHeader riskId={params.riskId} />
					<Paper
						elevation={1}
						sx={{
							flexGrow: 1,
							display: 'flex',
							flexDirection: 'column',
							backgroundColor: 'transparent',
							borderBottomLeftRadius: 6,
							borderBottomRightRadius: 6,
							boxShadow:
								'0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14)',
						}}
					>
						<Tabs
							variant="scrollable"
							value={matchedId}
							sx={{
								minHeight: '32px',
								'& .MuiTabs-flexContainer': {
									gap: 0.5,
								},
								'& .MuiTab-root': {
									backgroundColor: '#dddddd',
									color: '#424242',
									paddingBlock: 0,
									minHeight: '32px',
									fontSize: '1rem',
									fontWeight: '500',
								},
								'& .Mui-selected': {
									color: '#424242',
									backgroundColor: 'white',
								},
							}}
							slotProps={{
								indicator: {
									sx: {
										transform: 'scaleX(0.7)',
										backgroundColor: 'accent.main',
										height: '3px',
									},
								},
							}}
						>
							<Tab
								value={DetailsRoute.id}
								component={Link}
								to={DetailsRoute.to}
								replace
								label="Risk Details"
							/>
							<Tab
								value={DocumentsRoute.id}
								component={Link}
								to={DocumentsRoute.to}
								replace
								sx={(theme) => ({
									'& svg': {
										color: `color-mix(in srgb, ${theme.palette.warning.main} 70%, white)`,
									},
								})}
								label="Documents"
								disabled={
									!risk.noprCompleted ||
									!permissions.includes('risk.document.view')
								}
							/>
							<Tab
								value={DataRoute.id}
								component={Link}
								to={DataRoute.to}
								replace
								label="Extracted Data"
								disabled={
									!risk.riskDocuments.length ||
									risk.riskDocuments.some(
										(d) =>
											!['Processing', 'Completed', 'Validation Error'].includes(
												d.documentStatus,
											),
									) ||
									!permissions.includes('shared.data.view')
								}
							/>
							<Tab
								value={CalculationsRoute.id}
								component={Link}
								to={CalculationsRoute.to}
								replace
								label="BI Analysis"
								disabled={
									risk.riskDocuments.length === 0 ||
									!risk.riskDocuments.every(
										(document) => document.documentStatus === 'Completed',
									) ||
									!permissions.includes('risk.calculation.view')
								}
							/>
							<Tab
								value={ReportRoute.id}
								component={Link}
								to={ReportRoute.to}
								replace
								label="Report"
								disabled={
									risk.riskDocuments.length === 0 ||
									!hasReport ||
									!permissions.includes('risk.report')
								}
							/>
							<Tab
								value={ActivityRoute.id}
								component={Link}
								to={ActivityRoute.to}
								replace
								label="Activity"
								disabled={!permissions.includes('claim.activitylog.view')}
								sx={{ marginLeft: 'auto' }}
							/>
						</Tabs>
						<Box
							sx={{
								p: 2,
								flexGrow: 1,
								display: 'flex',
								flexDirection: 'column',
								backgroundColor: 'white',
								borderBottomLeftRadius: 6,
								borderBottomRightRadius: 6,
							}}
						>
							<Suspense fallback={<LinearProgress />}>
								<Outlet />
							</Suspense>
						</Box>
					</Paper>
				</Container>
			</ReasonDialogProvider>
		);
	},
});
